import React, { createContext, useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { authRoutes, protectedRoutes, publicRoutes } from 'src/config/routes';
import { toast } from 'src/hooks/use-toast';
import { authApi, refreshToken } from 'src/lib/api/auth/auth.api';
import { authEventManager } from 'src/lib/api/authEventManager';
import { api } from 'src/lib/api/axios.interceptor';
import { API_ENDPOINTS } from 'src/lib/api/constants';
import { useToast } from 'src/lib/toast-context';
import { collectRoutes } from 'src/lib/utils';
import { AuthState, LoginCredentials, User } from 'src/types/auth.types'; // Assuming User is exported
import { RouteConfig } from 'src/types/route-config.types';
import { API_RESP_MESSAGE } from './message';

// --- NEW: Helper to check for mock mode ---
// For Create React App: process.env.REACT_APP_MOCK_AUTH
// For Vite: import.meta.env.VITE_MOCK_AUTH
const IS_MOCK_MODE = process.env.REACT_APP_MOCK_AUTH === 'true';

// --- NEW: Mock user and token data ---
const mockUser: User = {
    _id: 'mock-user-123',
    usercode: "1234",
    name: 'Mock User',
    email: 'mock.user@example.com',
    role: 'admin',
    discom: "DISCOM",
    division: "executive"
    // Add any other user properties you need
};

const mockTokens = {
    accessToken: 'mock-access-token-string',
    refreshToken: 'mock-refresh-token-string',
};

// --- NEW: Mock functions to replace real ones in mock mode ---
const mockLogin = async (credentials: LoginCredentials): Promise<void> => {
    console.log('Mock login called with:', credentials);
    // In mock mode, login does nothing as the user is already "logged in"
    return Promise.resolve();
};

const mockLogout = async (): Promise<void> => {
    console.log('Mock logout called.');
    // In mock mode, a page refresh is the easiest way to "reset"
    // Or just do nothing if you want the session to persist
    window.location.reload();
    return Promise.resolve();
};

const mockRefreshUser = async (): Promise<void> => {
    console.log('Mock refreshUser called.');
    return Promise.resolve();
};


// --- The context and provider ---
interface AuthContextType extends AuthState {
    login: (credentials: LoginCredentials) => Promise<void>;
    logout: () => Promise<void>;
    refreshUser: () => Promise<void>;
    setUnAuthorized: (status?: boolean) => void;
}


export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
    children
}) => {
    const [state, setState] = useState<AuthState>({
        user: null,
        isAuthenticated: false,
        isLoading: true, // Start with loading true
        error: null,
        isLoginIng: false,
        unAuthorized: false
    });

    const navigate = useNavigate();
    const { pathname, search } = useLocation()
    const { showToast } = useToast()

    // console.log({ search, pathname });


    // Initialize auth state
    useEffect(() => {
        const initAuth = async () => {
            try {
                const _refreshToken = localStorage.getItem("refreshToken")
                // console.log("Auth Init :", { currentPath: pathname, _refreshToken });
                if (_refreshToken) {
                    const resp: any = _refreshToken && await api.post(API_ENDPOINTS.AUTH.REFRESH, { refreshToken: _refreshToken });
                    // console.log({ resp });
                    const { user, tokens }: any = resp.data?.data
                    localStorage.setItem('accessToken', tokens?.access?.token);
                    localStorage.setItem('refreshToken', tokens?.refresh?.token);
                    // console.log({ user, tokens });

                    setState({ user, isAuthenticated: true, isLoading: false, isLoginIng: false, error: null });
                    if (pathname === "/auth/login") {
                        navigate("/")
                    }
                    else navigate(pathname + search || "/")
                }
                else {
                    const PRIVATE_ROUTES = collectRoutes(protectedRoutes, "/")
                    const AUTH_ROUTES = collectRoutes(authRoutes, "/auth")
                    // console.log({ AUTH_ROUTES });

                    if (PRIVATE_ROUTES.includes(pathname)) {
                        navigate("/auth/login")
                    }
                    else if (AUTH_ROUTES.includes(pathname)) {
                        navigate(pathname + search)
                    }
                    else {
                        navigate(pathname + search || "/")
                    }

                    setState({
                        ...state,
                        isLoading: false
                    })
                }
            } catch (error) {
                setState({
                    ...state,
                    isLoading: false,
                    error: "Session Expired"
                })
            }
        };
        initAuth();
    }, []);

    // console.log({ user: state.user });


    // --- REAL API Functions (unchanged logic) ---
    const handleLogin = useCallback(async (credentials: LoginCredentials) => {
        setState(prev => ({ ...prev, isLoading: false, isLoginIng: true, error: null }));
        try {
            const resp = await authApi.login({ ...credentials, role: "consumer" });
            // console.log({ resp });

            if (resp?.code == 200) {
                const { user, tokens } = resp.data
                // console.log({ user, tokens });
                localStorage.setItem('accessToken', tokens?.access?.token);
                localStorage.setItem('refreshToken', tokens?.refresh?.token);
                setState({ user, isAuthenticated: true, isLoading: false, isLoginIng: false, error: null });
                navigate("/dashboard")
                showToast("Login successful", {
                    variant: "success",
                    position: "top-center",
                    duration: 1000
                })
            }
            else {
                showToast(API_RESP_MESSAGE(resp.code) || resp.message, {
                    variant: "error",
                    position: "top-center",
                    duration: 1000
                })
                setState({
                    user: null,
                    isAuthenticated: false,
                    isLoading: false,
                    isLoginIng: false,
                    error: resp.message || 'Login failed',
                });
            }

        } catch (error: any) {
            setState({
                user: null,
                isAuthenticated: false,
                isLoading: false,
                isLoginIng: false,
                error: error.response?.data?.message || 'Login failed',
            });

        }
    }, [navigate]);

    const handleLogout = useCallback(async () => {
        try {
            // await authApi.logout();
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            setState({ user: null, isAuthenticated: false, isLoading: false, isLoginIng: false, error: null });
            navigate('/auth/login');
        }
    }, [navigate]);

    const refreshUser = useCallback(async () => {

        const _refreshToken = localStorage.getItem("refreshToken")
        if (!state.isAuthenticated) return;
        try {
            if (_refreshToken) {
                const resp: any = _refreshToken && await api.post(API_ENDPOINTS.AUTH.REFRESH, { refreshToken: _refreshToken });
                // console.log({ resp });
                const { user, tokens }: any = resp.data?.data
                localStorage.setItem('accessToken', tokens?.access?.token);
                localStorage.setItem('refreshToken', tokens?.refresh?.token);
                // console.log({ user, tokens });
                authEventManager.emit('refresh-success');
                setState({ user, isAuthenticated: true, isLoading: false, isLoginIng: false, error: null });
                if (pathname === "/auth/login") {
                    navigate("/")
                }
                else navigate(pathname + search || "/")
            }
            else {
                handleLogout()
            }

        } catch (error) {
            console.error('Failed to refresh user:', error);
        }
    }, [state.isAuthenticated]);


    const setUnAuthorized = (status?: boolean) => {
        setState(prev => ({
            ...prev,
            unAuthorized: status || false
        }));
    }

    // --- MODIFIED: Conditionally provide mock or real functions ---
    const value: AuthContextType = {
        ...state,
        login: IS_MOCK_MODE ? mockLogin : handleLogin,
        logout: IS_MOCK_MODE ? mockLogout : handleLogout,
        refreshUser: IS_MOCK_MODE ? mockRefreshUser : refreshUser,
        setUnAuthorized: setUnAuthorized
    }

    return <AuthContext.Provider value={value}>
        {children}</AuthContext.Provider>;
};

// state.isLoading ? <div className='flex justify-center items-center h-screen w-screen'>
//             <div className='flex flex-col items-center space-y-4 w-[200px]'>
//                 <img src={'/applogo.png'} />
//                 <div className="w-3/4 h-1 bg-blue-500/30 rounded-lg relative overflow-hidden">
//                     <span className="absolute inset-y-0 left-0 bg-blue-600 rounded-lg animate-bar1"></span>
//                     <span className="absolute inset-y-0 left-0 bg-blue-600 rounded-lg animate-bar2"></span>
//                 </div>
//             </div>
//         </div> :